import type { ReactElement } from "react";
import { ListView } from "./ListView";
import { ListItemView } from "./ListItemView";
import { NoteView } from "./NoteView";
import { MealView } from "./MealView";
import { MemberView } from "./MemberView";
import { InviteView } from "./InviteView";
import { LinkDeviceView } from "./LinkDeviceView";
import { JoinWithCode } from "./JoinWithCode";
import { HelpView } from "./HelpView";
import { AccountView } from "./AccountView";
import { AppShell } from "./AppShell";
import type { DocMeta } from "../App";

interface FamilyInfo {
  familyId: string;
  familyName: string;
  memberName: string;
  memberId: number;
  publicKey: string;
}

type Screen =
  | { type: "tabs" }
  | { type: "list"; doc: DocMeta }
  | { type: "list-item"; doc: DocMeta; itemId: string }
  | { type: "note"; doc: DocMeta }
  | { type: "meal"; doc: DocMeta }
  | { type: "member"; name: string; isMe: boolean; avatarUrl: string | null; online: boolean; lastActiveAt: string | null }
  | { type: "invite" }
  | { type: "link-device" }
  | { type: "join-family" }
  | { type: "help" }
  | { type: "account" };

interface ScreenRouterProps {
  screen: Exclude<Screen, { type: "tabs" }>;
  family: FamilyInfo;
  syncVersion: number;
  authToken: string | null;
  apiUrl: string;
  onBack: () => void;
  onSetScreen: (s: Screen) => void;
  onFamilyChanged: (info: FamilyInfo | null) => void;
  initialCheckout?: "success" | "cancel" | null;
}

/**
 * Renders the appropriate detail screen based on `screen.type`.
 * All screens share the AppShell layout wrapper so the markup is defined once.
 */
export function ScreenRouter({
  screen,
  family,
  syncVersion,
  authToken,
  apiUrl,
  onBack,
  onSetScreen,
  onFamilyChanged,
  initialCheckout,
}: ScreenRouterProps): ReactElement {
  // Every ScreenRouter screen is a "secondary screen": no Footer, and a
  // pinned bottom Close button (rendered through AppShell's `overlays` slot,
  // never inside `.tab-content`, so it stays `position: fixed` to the
  // viewport per card 309) that invokes the same handler as the screen's
  // upper-right X.
  const shell = (children: React.ReactNode, onClose: () => void) => (
    <AppShell familyName={family.familyName} footer={false} overlays={
      <button type="button" className="close-button" onClick={onClose}>Close</button>
    }>
      {children}
    </AppShell>
  );

  if (screen.type === "list") {
    return shell(
      <ListView
        doc={screen.doc}
        memberId={family.memberId}
        syncVersion={syncVersion}
        onBack={onBack}
        onOpenItem={(itemId) => onSetScreen({ type: "list-item", doc: screen.doc, itemId })}
      />,
      onBack
    );
  }

  if (screen.type === "list-item") {
    const backToList = () => onSetScreen({ type: "list", doc: screen.doc });
    return shell(
      <ListItemView
        doc={screen.doc}
        itemId={screen.itemId}
        syncVersion={syncVersion}
        onBack={backToList}
      />,
      backToList
    );
  }

  if (screen.type === "note") {
    return shell(
      <NoteView doc={screen.doc} syncVersion={syncVersion} onBack={onBack} />,
      onBack
    );
  }

  if (screen.type === "meal") {
    return shell(
      <MealView
        doc={screen.doc}
        memberId={family.memberId}
        authToken={authToken}
        apiUrl={apiUrl}
        syncVersion={syncVersion}
        onBack={onBack}
      />,
      onBack
    );
  }

  if (screen.type === "member") {
    return shell(
      <MemberView
        name={screen.name}
        isMe={screen.isMe}
        avatarUrl={screen.avatarUrl}
        online={screen.online}
        lastActiveAt={screen.lastActiveAt}
        onBack={onBack}
        onLinkDevice={() => onSetScreen({ type: "link-device" })}
        onNameChanged={(newName) => {
          onFamilyChanged({ ...family, memberName: newName });
          onSetScreen({ ...screen, name: newName });
        }}
        onAvatarChanged={(newAvatarUrl) => {
          onSetScreen({ ...screen, avatarUrl: newAvatarUrl });
        }}
        onLeaveFamily={() => onFamilyChanged(null)}
      />,
      onBack
    );
  }

  if (screen.type === "invite") {
    return shell(<InviteView onBack={onBack} />, onBack);
  }

  if (screen.type === "link-device") {
    return shell(<LinkDeviceView onBack={onBack} />, onBack);
  }

  if (screen.type === "join-family") {
    return shell(
      <JoinWithCode
        onJoined={(info) => onFamilyChanged(info)}
        onCancel={onBack}
      />,
      onBack
    );
  }

  if (screen.type === "help") {
    return shell(<HelpView onBack={onBack} />, onBack);
  }

  // screen.type === "account"
  return shell(
    <AccountView onBack={onBack} justReturnedFromCheckout={initialCheckout ?? null} />,
    onBack
  );
}
